/** Construção do índice de pesquisa a partir dos modelos carregados.
 *
 *  A parte cara é ler o nome e o GlobalId de cada entidade. Num modelo
 *  grande são centenas de milhares de leituras, e não se pode fazer
 *  isso no fio principal de uma vez sem deixar a aplicação presa.
 *
 *  Por isso: em lotes, com uma pausa entre eles para o browser
 *  respirar, e com um tecto. Acima do tecto a pesquisa continua a
 *  funcionar com o que já indexou, e diz que é parcial, em vez de
 *  fingir que procurou em tudo.
 */

import { ehPesquisavel } from '@viewer/lib/entityFamily'
import type { EntradaPesquisa } from '@core/layout/search/searchIndex'

/** Acima disto não se indexa mais. Um modelo com mais entidades do que
 *  isto demora mais a indexar do que a pessoa está disposta a esperar,
 *  e quem trabalha assim usa o filtro, não a pesquisa rápida. */
export const TECTO_ENTIDADES = 120_000

/** Quantas entidades por lote. Um lote demasiado grande volta a
 *  prender o fio principal; demasiado pequeno e a indexação arrasta-se
 *  em pausas. */
export const TAMANHO_DO_LOTE = 2_000

export interface IndiceEstado {
  entradas: EntradaPesquisa[]
  /** Ficou por indexar alguma coisa por causa do tecto? */
  parcial: boolean
}

interface ModeloIndexavel {
  getItemsOfCategories: (
    padroes: RegExp[],
  ) => Promise<Record<string, number[] | undefined>>
  getItemsData: (
    ids: number[],
    opcoes: { attributesDefault: boolean },
  ) => Promise<unknown[]>
}

/** Lê um atributo de um registo do Fragments.
 *
 *  Os atributos chegam ou como valor directo ou embrulhados em
 *  `{ value }`, conforme a entidade e a versão do motor. Tratar os
 *  dois casos aqui evita repetir a verificação em todo o lado. */
export function atributo(dados: unknown, nome: string): string | null {
  if (typeof dados !== 'object' || dados === null) return null
  const bruto = (dados as Record<string, unknown>)[nome]
  if (typeof bruto === 'string') return bruto
  if (
    typeof bruto === 'object' &&
    bruto !== null &&
    'value' in bruto &&
    typeof (bruto as { value: unknown }).value === 'string'
  ) {
    return (bruto as { value: string }).value
  }
  return null
}

/** Transforma um lote de registos em entradas do índice. */
export function entradasDoLote(
  modelId: string,
  ifcClass: string,
  ids: number[],
  dados: unknown[],
): EntradaPesquisa[] {
  return ids.map((localId, i) => ({
    modelId,
    localId,
    ifcClass,
    nome: atributo(dados[i], 'Name'),
    // O Fragments expõe o GlobalId como `_guid`. O nome da norma
    // também se tenta, para o caso de vir de outra origem.
    guid: atributo(dados[i], '_guid') ?? atributo(dados[i], 'GlobalId'),
  }))
}

/** Indexa um modelo, em lotes, sem prender o fio principal.
 *
 *  `aoProgredir` é chamada a cada lote para a interface poder mostrar
 *  resultados enquanto o resto ainda está a ser lido.
 *  `cancelado` é consultada entre lotes: fechar a pesquisa ou trocar
 *  de modelo pára o trabalho a meio em vez de o deixar a correr. */
export async function indexarModelo(
  modelId: string,
  modelo: ModeloIndexavel,
  aoProgredir: (lote: EntradaPesquisa[]) => void,
  cancelado: () => boolean = () => false,
  tecto: number = TECTO_ENTIDADES,
): Promise<{ parcial: boolean }> {
  let total = 0
  const baldes = await modelo.getItemsOfCategories([/.*/])
  if (cancelado()) return { parcial: false }

  for (const [ifcClass, ids] of Object.entries(baldes)) {
    if (!ids || ids.length === 0) continue
    // Só entra o que se consegue seleccionar e ver. Sem esta linha,
    // procurar "wall" devolvia o Pset_WallCommon e o
    // Pset_ReinforcementBarPitchOfWall à frente das paredes, e clicar
    // num deles não fazia nada. Apanhado a testar com o modelo de
    // exemplo, não pelos testes com dados inventados.
    if (!ehPesquisavel(ifcClass)) continue
    for (let i = 0; i < ids.length; i += TAMANHO_DO_LOTE) {
      if (cancelado()) return { parcial: true }
      if (total >= tecto) return { parcial: true }
      const lote = ids.slice(i, i + TAMANHO_DO_LOTE)
      let dados: unknown[]
      try {
        dados = await modelo.getItemsData(lote, { attributesDefault: true })
      } catch {
        // Uma classe que o motor não saiba ler não pode levar o resto
        // do índice atrás: salta-se e continua.
        continue
      }
      if (cancelado()) return { parcial: true }
      aoProgredir(entradasDoLote(modelId, ifcClass, lote, dados))
      total += lote.length
      // Devolver o fio ao browser entre lotes. Sem isto, indexar um
      // modelo grande congela a interface durante segundos.
      await new Promise((r) => setTimeout(r, 0))
    }
  }
  return { parcial: total >= tecto }
}
